/**
 * A job's description as displayable text.
 *
 * `job.description` is NOT a string. It is a structured JobDescription value object:
 *
 *   { serviceTypes: [{ serviceName, description }, ...], freeText }
 *
 * Rendering it straight into JSX — `{job.description}` — throws "Objects are not valid
 * as a React child", which unmounts the tree and leaves a BLANK SCREEN. That is what
 * tapping the vehicle row on the phone did: the row expands, the description renders,
 * the page disappears. Two screens had the same bug, which is why this lives in one
 * place rather than being fixed twice.
 *
 * The job detail modal deliberately keeps its own richer rendering — a numbered list,
 * so a technician reads each customer complaint as its own line. This is for the places
 * that want one short paragraph.
 */

/**
 * Flatten a JobDescription to a single line of text.
 *
 * @returns {string} '' when there is nothing to show, so callers can test truthiness
 *   rather than having to know the shape.
 */
export const jobDescriptionText = (description) => {
    if (!description) return '';

    // Older clients and any stale cache may still hold a plain string from before the
    // structured migration. Passing that through unchanged is the whole point of the
    // check — it must not be treated as an object and silently become ''.
    if (typeof description === 'string') return description.trim();

    const serviceTypes = Array.isArray(description.serviceTypes) ? description.serviceTypes : [];
    const parts = serviceTypes
        .map(entry => {
            const name = (entry?.serviceName || '').trim();
            const detail = (entry?.description || '').trim();
            if (name && detail) return `${name}: ${detail}`;
            return name || detail;
        })
        .filter(Boolean);

    const freeText = (description.freeText || '').trim();
    if (freeText) parts.push(freeText);

    return parts.join(' · ');
};

export default jobDescriptionText;
